import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Grid from '@material-ui/core/Grid';
import {
  selectOrganisationAccessPoints,
  selectOrganisationSites,
} from '../../../../../redux/selectors/organisation';
import { selectCurrentPartner } from '../../../../../redux/selectors/authentication';
import { useParams } from 'react-router-dom';
import { Search, SearchStatus } from '../../../../../utils/Search';

import Button from '../../../../../components/Buttons/Button';
import Table from '../../../../../components/Table/Table';
import SquareDelete from '../../../../../components/SquareDelete/SquareDelete';

import {
  doDeleteOrganisationAccessPoint,
  doReadOrganisationAccessPoints,
} from '../../../../../redux/thunks/organisation';
import useBreakpoint from '../../../../../hooks/useBreakpoint';
import SquareEdit from '../../../../../components/SquareEdit/SquareEdit';
import ModalAlert from '../../../../../components/Modals/ModalAlert';
import { configTypeRender } from '../../../../../helpers/renderers';
import EditAccessPointModal from './AddOrEditAccessPointModal/EditAccessPointModal';
import AddAccessPointModal from './AddOrEditAccessPointModal/AddAccessPointModal';
import useIsMobile from '../../../../../hooks/useIsMobile';
import MobileTable from '../../../../../components/MobileTable/MobileTable';
import FlexRow from '../../../../../components/FlexBox/FlexRow';
import SDialog from '../../../../../components/SDialog/SDialog';
import Typography from '../../../../../components/Typography/Typography';
import SButton from '../../../../../components/SButton/SButton';
import { formatUnderscoreText } from '../../../../../helpers/utils/formValidations';

const AccessPointTab = () => {

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const [showAccessPointPopup, setShowAccessPointPopup] = useState(false);
  const [showEditAccessPointPopup, setShowEditAccessPointPopup] = useState(false);
  const [showDeleteAccessPointPopup, setShowDeleteAccessPointPopup] = useState(false);
  const [accessPoint, setAccessPoint] = useState(null);

  // replaced useImmer → useState
  const [filters, setFilters] = useState({
    name: '',
    id: '',
    siteName: '',
    configuration: '',
    networkName: '',
    status: '',
  });

  const [pagination, setPagination] = useState({ total: 0, currentPage: 1, perPage: 7 });

  const { customerId } = useParams();

  const { accessPoints, currentUser, sites } = useSelector((state) => ({
    accessPoints: selectOrganisationAccessPoints(state),
    currentUser: selectCurrentPartner(state),
    sites: selectOrganisationSites(state),
  }));

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: accessPoints?.length ? accessPoints?.length : 0,
    }));
  }, [accessPoints?.length]);

  const columns = useMemo(() => {
    const commonCols = [
      { title: 'Id', field: 'id', name: 'id' },
      {
        title: 'Access Point Name',
        field: 'name',
        name: 'name',
      },
      {
        title: 'Site',
        name: 'siteName',
        field: 'siteName',
      },
      {
        title: 'Network',
        name: 'networkName',
        field: 'networkName',
      },
      {
        title: 'Status',
        field: 'resourceState',
        render: (columnProps, rowData) => formatUnderscoreText(rowData.resourceState),
        renderM: (columnProps, rowData) => (<Typography style={{ textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '75%' }} variant={"b3R"}>{formatUnderscoreText(rowData.resourceState)}</Typography>),
        name: 'status',
      },
      {
        title: 'Configuration',
        field: 'configuration',
        render: (columnProps, rowData) => configTypeRender(rowData.configuration),
        renderM: (columnProps, rowData) => (<Typography style={{ textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '75%' }} variant={"b3R"}>{configTypeRender(rowData.configuration)}</Typography>),
      }
    ]

    const actions = [
      {
        title: 'Action',
        render: (columnProps, rowData) => (
          <FlexRow gap='0.5rem' justifyContent='end'>
            <SquareEdit
              color={'#ff535a'}
              onPress={() => {
                setAccessPoint(rowData);
                togglePopup('EditAccessPoint');
              }}
              style={{ width: '2rem', height: '2rem' }}
            />
            <SquareDelete
              color={'#ff535a'}
              onPress={() => {
                setAccessPoint(rowData);
                togglePopup('DeleteAccessPoint');
              }}
              style={{ width: '2rem', height: '2rem', padding: '0' }}
            />
          </FlexRow>
        ),
        renderM: (columnProps, rowData) => (
          <FlexRow gap='0.5rem' justifyContent='end'>
            <SquareEdit
              color={'#ff535a'}
              onPress={() => {
                setAccessPoint(rowData);
                togglePopup('EditAccessPoint');
              }}
              style={{ width: '2rem', height: '2rem' }}
            />
            <SquareDelete
              color={'#ff535a'}
              onPress={() => {
                setAccessPoint(rowData);
                togglePopup('DeleteAccessPoint');
              }}
              style={{ width: '2rem', height: '2rem', padding: '0' }}
            />
          </FlexRow>
        ),
      }
    ]

    return {
      allCols: [...commonCols, ...actions],
      commonColumns: commonCols,
      actions: actions
    }

  }, [])

  const togglePopup = (popupName) => {
    if (popupName === 'AddAccessPoint') {
      setShowAccessPointPopup((prev) => !prev);
      if (showAccessPointPopup) setAccessPoint(null);
    }

    if (popupName === 'EditAccessPoint') {
      setShowEditAccessPointPopup((prev) => !prev);
      if (showEditAccessPointPopup) setAccessPoint(null);
    }

    if (popupName === 'DeleteAccessPoint') {
      setShowDeleteAccessPointPopup((prev) => !prev);
      if (showDeleteAccessPointPopup) setAccessPoint(null);
    }
  };

  const point = useBreakpoint();

  const mobileFiltersConfig = useMemo(() => {
    return [
      { name: "Access Point Name", key: "name", type: "search" },
      { name: "ID", key: "id", type: "search" },
      { name: "Site", key: "siteName", type: "search" },
      { name: "Network", key: "networkName", type: "search" },
      { name: "Status", key: "status", type: "search" },
    ]
  }, [])

  useEffect(() => {
    if (currentUser) {
      dispatch(
        doReadOrganisationAccessPoints({
          partnerId: currentUser.partnerId,
          organisationId: customerId,
          displayNotification: false,
        }),
      );
    }
  }, [dispatch, point, currentUser, customerId]);

  const handleDeleteAccessPoint = () => {
    dispatch(
      doDeleteOrganisationAccessPoint({
        partnerId: currentUser.partnerId,
        organisationId: customerId,
        accessPointId: accessPoint.id,
      }),
    );
    setAccessPoint(null);
    setShowDeleteAccessPointPopup(false);
  };

  const hSearchChange = (e) => {
    const { name, value } = e.target;
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const search = {
    onChange: hSearchChange,
  };

  const filteredData = useCallback(() => {
    let data = accessPoints ? accessPoints : [];

    if (filters.id !== '') data = Search(data, 'id', filters.id);
    if (filters.name !== '') data = Search(data, 'name', filters.name);
    if (filters.siteName !== '') data = Search(data, 'siteName', filters.siteName);
    if (filters.networkName !== '') data = Search(data, 'networkName', filters.networkName);
    if (filters.configuration !== '') data = Search(data, 'configuration', filters.configuration);
    if (filters.status !== '') data = Search(data, 'resourceState', filters.status);


    if (!isMobile) {
      data = data.slice(
        (pagination.currentPage - 1) * 7,
        pagination.currentPage * pagination.perPage,
      );
    }

    return data;
  }, [filters, pagination.currentPage, pagination.perPage, accessPoints, isMobile]);

  const changePage = (p) => {
    const { page, per_page } = p;
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      perPage: per_page,
    }));
  };

  return (
    <Fragment>
      <Grid container className="spacer__1" />
      <Grid container>
        {showAccessPointPopup && (
          <AddAccessPointModal
            {...{
              showAccessPointPopup,
              togglePopup,
              customerId,
              partnerId: currentUser?.partnerId,
            }}
          />
        )}
        {showEditAccessPointPopup && (
          <EditAccessPointModal
            {...{
              showEditAccessPointPopup,
              togglePopup,
              accessPoint,
              customerId,
              partnerId: currentUser?.partnerId,
            }}
          />
        )}
        {showDeleteAccessPointPopup && (
          <SDialog
            title='Delete Access Point/ s'
            open={showDeleteAccessPointPopup}
            onClose={() => {
              setShowDeleteAccessPointPopup(false);
            }}
            titleConfig={{
              variant: isMobile ? 'h6M' : 'h5M'
            }}
            secondaryButtonConfig={{
              text: 'Cancel',
              onClick: () => {
                setShowDeleteAccessPointPopup(false);
              }
            }}
            primaryButtonConfig={{
              text: 'Delete',
              color: 'error',
              onClick: handleDeleteAccessPoint
            }}
          >
            <Typography variant={isMobile ? "b2M" : "b1M"} style={{ padding: '1.5rem 0' }}>Are you sure you want to delete {accessPoint ? accessPoint.name : ''} ? This
              action is non reversible and will cause you to lose all data associated with this
              AccessPoint/ s</Typography>
          </SDialog>

        )}

        <FlexRow justifyContent='space-between' styles={{ marginBottom: '1.5rem' }}>
          <Typography variant={"h7SB"} color='var(--grey-800)'>Access Points</Typography>
          <SButton
            color={"info"}
            onClick={() => {
              togglePopup('AddAccessPoint');
            }}
            disabled={sites == null || sites.length === 0}
          >Add Access Points</SButton>
        </FlexRow>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {isMobile ?
            <MobileTable
              columns={columns?.commonColumns}
              actions={columns?.actions}
              data={filteredData()}
              filters={filters}
              setFilters={setFilters}
              filtersConfig={mobileFiltersConfig}
              emptyMessage={'No Access Points Found'}
            /> :
            <Table
              columns={columns?.allCols}
              data={filteredData()}
              defaultMessage={'No Access Points Found'}
              pagination={pagination}
              options={{ search }}
              changePage={changePage}
            />}
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default AccessPointTab;
